import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { isAllowedOrigin } from './iframeUtils';
import MessageDebugPanel from './components/MessageDebugPanel';

const PostMessageContext = createContext(null);

function formatTime() {
  const d = new Date();
  return d.toLocaleTimeString('en-US', { hour12: false }) + '.' + String(d.getMilliseconds()).padStart(3, '0');
}

function summarize(data) {
  try {
    return JSON.stringify(data, null, 2);
  } catch { return String(data); }
}

export function PostMessageProvider({ children }) {
  const [showMenu, setShowMenu] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [messageLog, setMessageLog] = useState([]);
  const handlersRef = useRef({});
  const saveResolverRef = useRef(null);
  const uploadResolverRef = useRef(null);
  const widgetResolverRef = useRef(null);
  const widgetSaveResolverRef = useRef(null);

  const logMessage = useCallback((dir, type, data) => {
    setMessageLog(prev => [...prev.slice(-200), {
      dir, type, time: formatTime(), preview: summarize(data),
    }]);
  }, []);

  // Send a postMessage to parent and log it
  const sendToParent = useCallback((data) => {
    logMessage('out', data.type || '?', data);
    window.parent.postMessage(data, '*');
  }, [logMessage]);

  // Built-in handlers
  handlersRef.current['toggle_menu'] = useCallback(() => {
    setShowMenu(prev => !prev);
  }, []);

  handlersRef.current['toggle_message_debug_ui'] = useCallback(() => {
    setShowDebug(prev => !prev);
  }, []);

  handlersRef.current['save_res'] = useCallback((data) => {
    // Route to widget save resolver if active, else brand save resolver
    if (widgetSaveResolverRef.current) {
      widgetSaveResolverRef.current(data);
      widgetSaveResolverRef.current = null;
    } else if (saveResolverRef.current) {
      saveResolverRef.current(data);
      saveResolverRef.current = null;
    }
  }, []);

  handlersRef.current['brand_widgets'] = useCallback((data) => {
    if (widgetResolverRef.current) {
      widgetResolverRef.current(data.brand_widgets || data.payload?.brand_widgets || []);
      widgetResolverRef.current = null;
    }
  }, []);

  handlersRef.current['saving_brand_widgets'] = useCallback(() => {
    // Acknowledgement that parent started saving — no action needed
  }, []);

  handlersRef.current['upload_res'] = useCallback((data) => {
    if (uploadResolverRef.current) {
      uploadResolverRef.current(data);
      uploadResolverRef.current = null;
    }
  }, []);

  // Send save_brand to parent, returns a promise that resolves on save_res
  const saveBrand = useCallback((brandData) => {
    return new Promise((resolve, reject) => {
      saveResolverRef.current = resolve;
      sendToParent({ type: 'save_brand', payload: brandData });
      setTimeout(() => {
        if (saveResolverRef.current) {
          saveResolverRef.current = null;
          reject(new Error('Save timed out — no response from parent.'));
        }
      }, 10000);
    });
  }, [sendToParent]);

  // Send upload_file to parent, returns a promise that resolves on upload_res
  const uploadFile = useCallback((file, brandId) => {
    return new Promise((resolve, reject) => {
      uploadResolverRef.current = resolve;
      sendToParent({ type: 'upload_file', files: [file], brand_id: brandId });
      setTimeout(() => {
        if (uploadResolverRef.current) {
          uploadResolverRef.current = null;
          reject(new Error('Upload timed out — no response from parent.'));
        }
      }, 30000);
    });
  }, [sendToParent]);

  // Request widgets from parent
  const getWidgets = useCallback(() => {
    return new Promise((resolve, reject) => {
      widgetResolverRef.current = resolve;
      sendToParent({ type: 'get_brand_widgets' });
      setTimeout(() => {
        if (widgetResolverRef.current) {
          widgetResolverRef.current = null;
          reject(new Error('Get widgets timed out.'));
        }
      }, 10000);
    });
  }, [sendToParent]);

  // Save widget to parent (key='new' for creation)
  const saveWidgetToParent = useCallback((widgetData) => {
    return new Promise((resolve, reject) => {
      widgetSaveResolverRef.current = resolve;
      sendToParent({
        type: 'update_brand_widgets',
        key: widgetData.key,
        title: widgetData.title,
        engine_id: widgetData.engine_id,
        details: widgetData.details,
      });
      setTimeout(() => {
        if (widgetSaveResolverRef.current) {
          widgetSaveResolverRef.current = null;
          reject(new Error('Widget save timed out.'));
        }
      }, 10000);
    });
  }, [sendToParent]);

  // Single global listener — dispatches by message type, logs all incoming
  useEffect(() => {
    const onMessage = (event) => {
      const data = event.data;
      const type = (data && typeof data === 'object') ? data.type : String(data);

      // Filter out noisy webpack dev messages
      if (type && type.toLowerCase().includes('webpack')) return;

      // Log ALL incoming messages (even rejected ones)
      logMessage('in', type || '?', data);

      if (!isAllowedOrigin(event.origin)) return;
      if (type && handlersRef.current[type]) {
        handlersRef.current[type](data, event);
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [logMessage]);

  const registerHandler = useCallback((type, handler) => {
    handlersRef.current[type] = handler;
    return () => { delete handlersRef.current[type]; };
  }, []);

  return (
    <PostMessageContext.Provider value={{ showMenu, registerHandler, saveBrand, uploadFile, sendToParent, getWidgets, saveWidgetToParent }}>
      {children}
      {showDebug && <MessageDebugPanel messages={messageLog} onClose={() => setShowDebug(false)} />}
    </PostMessageContext.Provider>
  );
}

export const usePostMessage = () => useContext(PostMessageContext);
